import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranscribeDictationMutation } from '@/core/services/medconnect-agent-service'

export type DictationState = 'idle' | 'recording' | 'transcribing'
export type DictationError = 'permission' | 'failed' | 'empty'

/** A dictation is a short request; stop on our own so a forgotten mic never records for long. */
export const MAX_DICTATION_MS = 60_000

/** First recording format the browser supports (Chrome/Firefox: Opus in WebM, Safari: AAC in MP4). */
function pickMimeType(): string | undefined {
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) => MediaRecorder.isTypeSupported?.(type))
}

/** Whether this browser can record audio at all (otherwise the mic button is not shown). */
export function isDictationSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}

/**
 * Voice input: tap to record, tap again (or wait {@link MAX_DICTATION_MS}) to stop; the recording is
 * transcribed by the backend and handed to `onText`. The microphone is released as soon as recording stops.
 */
export function useDictation(onText: (text: string) => void) {
  const [state, setState] = useState<DictationState>('idle')
  const [error, setError] = useState<DictationError | null>(null)
  const [transcribe] = useTranscribeDictationMutation()
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<number | undefined>(undefined)
  const mountedRef = useRef(true)
  const onTextRef = useRef(onText)
  onTextRef.current = onText

  const releaseMicrophone = useCallback(() => {
    window.clearTimeout(timerRef.current)
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const sendRecording = useCallback(
    async (audio: Blob) => {
      if (!mountedRef.current) return
      if (audio.size === 0) return finish('empty')
      setState('transcribing')
      try {
        const { text } = await transcribe(audio).unwrap()
        if (!mountedRef.current) return
        if (text) onTextRef.current(text)
        finish(text ? null : 'empty')
      } catch {
        finish('failed')
      }
    },
    [transcribe]
  )

  function finish(nextError: DictationError | null): void {
    if (!mountedRef.current) return
    setError(nextError)
    setState('idle')
  }

  const start = useCallback(async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!mountedRef.current) return stream.getTracks().forEach((track) => track.stop())
      streamRef.current = stream
      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      const chunks: Blob[] = []
      recorder.ondataavailable = (event) => event.data.size > 0 && chunks.push(event.data)
      recorder.onstop = () => {
        releaseMicrophone()
        void sendRecording(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }))
      }
      recorderRef.current = recorder
      recorder.start()
      setState('recording')
      timerRef.current = window.setTimeout(() => recorder.state === 'recording' && recorder.stop(), MAX_DICTATION_MS)
    } catch (err) {
      releaseMicrophone()
      const name = (err as DOMException)?.name
      finish(name === 'NotAllowedError' || name === 'SecurityError' ? 'permission' : 'failed')
    }
  }, [releaseMicrophone, sendRecording])

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }, [])

  const toggle = useCallback(() => (state === 'recording' ? stop() : state === 'idle' ? void start() : undefined), [state, start, stop])

  // Leaving the page mid-recording drops the recording and frees the microphone.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      const recorder = recorderRef.current
      if (recorder) recorder.onstop = null
      if (recorder?.state === 'recording') recorder.stop()
      releaseMicrophone()
    }
  }, [releaseMicrophone])

  return { state, error, toggle }
}
